<template>
  <div>
    <header>
      <div class="back-link-row">
        <router-link to="/" class="back-link">&larr; Back to Recipes</router-link>
      </div>
      <h1>{{ recipe.title }}</h1>
      <p class="category">{{ recipe.category }}</p>
      <p v-if="recipe.contributor" class="contributor">Contributed by: {{ recipe.contributor }}</p>
    </header>
    <main>
      <section class="ingredients">
        <div class="section-heading">
          <h2>Ingredients</h2>
          <button
            v-if="recipeYield"
            type="button"
            class="reset-scale"
            :disabled="isOriginalAmount"
            @click="resetScale"
          >
            Reset
          </button>
        </div>
        <section v-if="recipeYield" class="recipe-calculator" aria-labelledby="calculator-title">
          <div>
            <p id="calculator-title" class="calculator-eyebrow">Recipe calculator</p>
            <p class="calculator-copy">This recipe starts at <strong>{{ recipeYield.label }}</strong>. Adjust it to fit your table.</p>
          </div>
          <div class="amount-control">
            <button type="button" class="amount-stepper" aria-label="Make less" @click="adjustAmount(-amountStep)">−</button>
            <label>
              <span class="sr-only">Desired {{ recipeYield.unit }}</span>
              <input v-model.number="desiredAmount" type="number" min="0.25" step="0.25" inputmode="decimal">
            </label>
            <span class="amount-unit">{{ recipeYield.unit }}</span>
            <button type="button" class="amount-stepper" aria-label="Make more" @click="adjustAmount(amountStep)">+</button>
          </div>
        </section>
        <ul>
          <li v-for="(ing, index) in displayedIngredients" :key="`${index}-${ing}`">{{ ing }}</li>
        </ul>
      </section>
      <section class="instructions">
        <h2>Instructions</h2>
        <p>{{ recipe.instructions }}</p>
      </section>
      <div class="actions">
        <button @click="printRecipe" class="print-btn">Print Recipe</button>
        <button @click="shareRecipe" class="share-btn">Share Link</button>
      </div>
    </main>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'
import { fetchFromApi } from '@/services/api'
import { getRecipeYield, scaleIngredient } from '@/services/recipeCalculator'

export default {
  name: 'RecipeDetail',
  props: ['id'],
  setup(props) {
    const recipe = ref({})
    const desiredAmount = ref(null)
    const apiUrl = process.env.VUE_APP_API_URL

    const recipeYield = computed(() => getRecipeYield(recipe.value))
    const multiplier = computed(() => {
      if (!recipeYield.value || !Number.isFinite(desiredAmount.value) || desiredAmount.value <= 0) return 1
      return desiredAmount.value / recipeYield.value.amount
    })
    const displayedIngredients = computed(() => {
      const ingredients = Array.isArray(recipe.value.ingredients) ? recipe.value.ingredients : []
      return recipeYield.value ? ingredients.map((ingredient) => scaleIngredient(ingredient, multiplier.value)) : ingredients
    })
    const amountStep = computed(() => recipeYield.value && recipeYield.value.amount >= 4 ? 1 : 0.25)
    const isOriginalAmount = computed(() => !recipeYield.value || desiredAmount.value === recipeYield.value.amount)

    watch(recipeYield, (yieldAmount) => {
      desiredAmount.value = yieldAmount ? yieldAmount.amount : null
    }, { immediate: true })

    onMounted(async () => {
      try {
        if (apiUrl) {
          recipe.value = await fetchFromApi(`recipes/${props.id}`)
        } else {
          const fileId = encodeURIComponent(props.id)
          const response = await fetch(`/recipes/${fileId}.json`)
          if (!response.ok) throw new Error('Recipe not found')
          recipe.value = await response.json()
        }
      } catch (error) {
        console.error('Failed to load recipe:', error)
      }
    })

    const printRecipe = () => {
      window.print()
    }

    const shareRecipe = () => {
      const url = window.location.href
      navigator.clipboard.writeText(url).then(() => {
        alert('Link copied to clipboard!')
      })
    }

    const adjustAmount = (change) => {
      const currentAmount = Number(desiredAmount.value) || recipeYield.value.amount
      desiredAmount.value = Math.max(0.25, Math.round((currentAmount + change) * 4) / 4)
    }

    const resetScale = () => {
      desiredAmount.value = recipeYield.value.amount
    }

    return {
      recipe,
      recipeYield,
      desiredAmount,
      displayedIngredients,
      amountStep,
      isOriginalAmount,
      adjustAmount,
      resetScale,
      printRecipe,
      shareRecipe
    }
  }
}
</script>

<style>
header {
  margin-bottom: 30px;
  text-align: center;
  padding: 24px;
  background: var(--surface-alt);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.back-link {
  display: inline-block;
  margin-bottom: 20px;
  color: var(--accent);
  text-decoration: none;
}

.back-link-row {
  margin-top: 8px;
}

.back-link:hover {
  text-decoration: underline;
}

.category {
  color: var(--muted-text);
  font-style: italic;
  margin: 10px 0;
  font-size: 0.95rem;
}

.contributor {
  color: var(--muted-text);
  font-size: 14px;
}

.ingredients, .instructions {
  margin-bottom: 30px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.reset-scale {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--accent);
  border-radius: 999px;
  padding: 5px 12px;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
}

.reset-scale:disabled {
  color: var(--muted-text);
  cursor: default;
  opacity: 0.65;
}

.recipe-calculator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 0 0 22px;
  padding: 18px 20px;
  border: 1px solid var(--border);
  border-left: 4px solid var(--accent);
  border-radius: 12px;
  background: var(--surface-alt);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.calculator-eyebrow {
  margin: 0 0 2px;
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.calculator-copy {
  margin: 0;
  color: var(--muted-text);
  font-size: 0.95rem;
}

.calculator-copy strong {
  color: var(--text);
}

.amount-control {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  background: var(--input-bg);
}

.amount-control input {
  width: 68px;
  border: 0;
  border-right: 1px solid var(--input-border);
  background: transparent;
  color: var(--text);
  padding: 9px 5px 9px 10px;
  font: inherit;
  font-weight: 700;
}

.amount-control input:focus {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

.amount-unit {
  padding: 0 10px;
  color: var(--muted-text);
  font-size: 0.85rem;
  font-weight: 700;
}

.amount-stepper {
  width: 36px;
  align-self: stretch;
  border: 0;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  font: inherit;
  font-size: 1.25rem;
  font-weight: 700;
}

.amount-stepper:hover,
.amount-stepper:focus-visible {
  background: var(--accent);
  color: var(--accent-contrast);
  outline: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

ul {
  padding-left: 20px;
}

li {
  margin-bottom: 5px;
}

.actions {
  text-align: center;
  margin-top: 30px;
}

.print-btn, .share-btn {
  padding: 10px 20px;
  margin: 0 10px;
  background: var(--accent);
  color: var(--accent-contrast);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.print-btn:hover, .share-btn:hover {
  background: var(--accent-strong);
}

@media print {
  .back-link, .actions, .recipe-calculator, .reset-scale {
    display: none;
  }
  header {
    text-align: left;
    box-shadow: none;
    border: none;
  }
}

@media (max-width: 600px) {
  .recipe-calculator {
    align-items: stretch;
    flex-direction: column;
  }

  .amount-control {
    align-self: flex-start;
  }
}
</style>
